import { Circle, Clear, NotificationsNoneOutlined } from '@mui/icons-material'
import { Chip, IconButton, Menu } from '@mui/material'
import { isEmpty } from 'lodash'
import { useContext, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { deleteNotification, Notification, NotificationType, UserRole } from '../../../api'
import FetchError from '../../../assets/fetch_error.svg'
import { AppStateContext } from '../../../state/AppProvider'
import { getSmartTimeDifference } from '../../../utils/timeDifference'
import styles from './NotificationsMenu.module.css'

type NotificationsMenuProps = {
  anchorNotificationsEl: HTMLElement | null
  openNotificationsMenu: boolean
  handleCloseNotifications: () => void
  anchorOrigin: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'right'
  }
  transformOrigin: {
    vertical: 'top' | 'bottom'
    horizontal: 'left' | 'right'
  }
  className?: string
}

export const NotificationsMenu: React.FC<NotificationsMenuProps> = ({
  anchorNotificationsEl,
  openNotificationsMenu,
  handleCloseNotifications,
  anchorOrigin,
  transformOrigin,
  className,
}: NotificationsMenuProps) => {
  const [selectedFilter, setSelectedFilter] = useState<'all' | 'unread'>('all')
  const appStateContext = useContext(AppStateContext)

  const handleSelectFilter = (filter: 'all' | 'unread') => {
    setSelectedFilter(filter)
  }
  const notifications = appStateContext?.state.notifications
  const navigate = useNavigate()
  const hasFetchNotificationsError = appStateContext?.state.hasFetchNotificationsError
  const isAdminOrOUOrchestrator =
    appStateContext?.state.user?.role === UserRole.ADMIN ||
    appStateContext?.state.user?.role === UserRole.DECODER ||
    appStateContext?.state.user?.role === UserRole.OU_ORCHESTRATOR

  const filteredNotifications = useMemo(
    () =>
      notifications?.filter(notification => {
        if (selectedFilter === 'all') {
          return true
        } else if (selectedFilter === 'unread') {
          return !notification.is_read
        }
      }),
    [notifications, selectedFilter],
  )

  const handleClickDeleteNotification = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    deleteNotification(notificationId).then(() => {
      appStateContext?.dispatch({ type: 'DELETE_NOTIFICATION', payload: notificationId })
    })
  }

  const isNotificationActionable = (notification: Notification) => {
    switch (notification.type) {
      case NotificationType.ACCESS_REQUEST_CREATED:
      case NotificationType.ACCESS_REQUEST_APPROVED:
      case NotificationType.ACCESS_REQUEST_REJECTED:
        return isAdminOrOUOrchestrator
      case NotificationType.EXPERIMENT_COMPLETED:
        return Boolean(notification.parent_id)
      default:
        return false
    }
  }

  const handleClickNotification = (notification: Notification) => {
    switch (notification.type) {
      case NotificationType.ACCESS_REQUEST_CREATED:
      case NotificationType.ACCESS_REQUEST_APPROVED:
      case NotificationType.ACCESS_REQUEST_REJECTED:
        if (isAdminOrOUOrchestrator) {
          navigate('/app/access-request-management')
          handleCloseNotifications()
        }
        break
      case NotificationType.EXPERIMENT_COMPLETED:
        if (notification.parent_id) {
          navigate(`/app/micro-learnings/${encodeURIComponent(notification.parent_id)}`)
          handleCloseNotifications()
        }
        break
      default:
        break
    }
  }

  const getNotificationItemClassName = (notification: Notification) => {
    const isRead = notification.is_read
    if (isNotificationActionable(notification)) {
      return isRead ? styles.menuNotificationItemRead : styles.menuNotificationItem
    }
    return isRead ? styles.menuNotificationItemReadNoHover : styles.menuNotificationItemNoHover
  }

  return (
    <Menu
      className={className}
      id="notifications-menu"
      anchorEl={anchorNotificationsEl}
      open={openNotificationsMenu}
      onClose={handleCloseNotifications}
      anchorOrigin={{
        vertical: anchorOrigin.vertical,
        horizontal: anchorOrigin.horizontal,
      }}
      transformOrigin={{
        vertical: transformOrigin.vertical,
        horizontal: transformOrigin.horizontal,
      }}
      slotProps={{
        paper: {
          className: styles.menuContainer,
        },
      }}>
      <div className={styles.menuContentContainer}>
        <div className={styles.menuContentHeader}>
          <p className={styles.menuTitle}>Notifications</p>
          <div className={styles.menuFilters}>
            <Chip
              label="All"
              className={selectedFilter === 'all' ? styles.menuFilterChipSelected : styles.menuFilterChip}
              onClick={() => handleSelectFilter('all')}
            />
            <Chip
              label="Unread"
              className={selectedFilter === 'unread' ? styles.menuFilterChipSelected : styles.menuFilterChip}
              onClick={() => handleSelectFilter('unread')}
            />
          </div>
        </div>
        <div className={styles.menuNotifications}>
          {selectedFilter === 'unread' && !hasFetchNotificationsError && !isEmpty(filteredNotifications) && (
            <p className={styles.menuSubtitle}>New</p>
          )}
          {hasFetchNotificationsError ? (
            <div className={styles.noExperimentsContainer}>
              <img
                src={FetchError}
                className={styles.noExperimentsImage}
              />
              <p className={styles.noExperimentsHeader}>Error Fetching Notifications</p>
              <p className={styles.noExperimentsText}>Unable to load notifications. Please try again later</p>
            </div>
          ) : isEmpty(filteredNotifications) && !hasFetchNotificationsError ? (
            <p className={styles.menuNotificationsEmpty}>
              {selectedFilter === 'all' ? 'You have no notifications' : 'You have no unread notifications'}
            </p>
          ) : (
            !hasFetchNotificationsError &&
            filteredNotifications?.map(notification => (
              <div
                className={getNotificationItemClassName(notification)}
                key={notification.id}
                onClick={() => handleClickNotification(notification)}>
                <div className={styles.menuNotificationItemContent}>
                  <div className={styles.menuNotificationItemContentIcons}>
                    <Circle
                      className={
                        notification.is_read
                          ? styles.menuNotificationItemContentIconCircleRead
                          : styles.menuNotificationItemContentIconCircle
                      }
                    />
                    <div
                      className={
                        notification.is_read
                          ? styles.menuNotificationItemContentIconBellRead
                          : styles.menuNotificationItemContentIconBell
                      }>
                      <NotificationsNoneOutlined />
                    </div>
                  </div>
                  <div className={styles.menuNotificationItemContentText}>
                    <p className={styles.menuNotificationItemTitle}>{notification.title}</p>
                    <p className={styles.menuNotificationItemMessage}>{notification.message}</p>
                    <p className={styles.menuNotificationItemTime}>
                      {getSmartTimeDifference(new Date(notification.created_at))}
                    </p>
                  </div>
                </div>
                <IconButton
                  className={styles.menuNotificationItemContentDeleteButton}
                  data-testid={`delete-notification-${notification.id}`}
                  onClick={event => handleClickDeleteNotification(notification.id, event)}>
                  <Clear fontSize="small" />
                </IconButton>
              </div>
            ))
          )}
        </div>
      </div>
    </Menu>
  )
}
